import { Component, OnInit, AfterViewInit, inject, viewChild, signal } from '@angular/core';

import { ReactiveFormsModule, FormsModule, FormControl, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { GridModule, DataBindingDirective } from '@progress/kendo-angular-grid';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { ToolBarModule } from '@progress/kendo-angular-toolbar';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { SVGIcon, fileExcelIcon, filterIcon, chevronUpIcon, chevronDownIcon } from '@progress/kendo-svg-icons';
import { SortDescriptor } from '@progress/kendo-data-query';
import { process } from '@progress/kendo-data-query';
import { RowClassArgs } from '@progress/kendo-angular-grid';

import { ApiService } from '../../../services/api.service';
import { KENDO_GRID_PAGE_SIZES } from '../../../constants/api.constant';
import { IChatSession } from '../../../interfaces/i-chat-session';
import { ISensitivityLabel, ISensitivityLabelExpanded } from '../../../interfaces/i-sensitivity-label';
import { AlertService } from '../../../shared/alert/alert.service';
import { InfoButtonTooltipComponent } from '../../../shared/info-button-tooltip/info-button-tooltip.component';
import { SensitivityLabelsComponent } from '../../../shared/sensitivity-labels/sensitivity-labels.component';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { AgentService } from '../../agent/services/agent.service';
import { ChatSessionService } from '../services/chat-session.service';
import { AlertEnum } from '../../../shared/alert/alert.enum';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IGrantedAgent } from "../../../interfaces/i-agents";


@Component({
  selector: 'app-quick-edit-session',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    GridModule,
    ExcelExportModule,
    ToolBarModule,
    InputsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatInputModule,
    SensitivityLabelsComponent,
    InfoButtonTooltipComponent,
    KENDO_BUTTONS
],
  templateUrl: './quick-edit-session.component.html',
  styleUrl: './quick-edit-session.component.scss'
})
export class QuickEditSessionComponent implements OnInit, AfterViewInit {
  dataBinding = viewChild<DataBindingDirective>(DataBindingDirective);
  gridData: IGrantedAgent[] = [];
  gridView: IGrantedAgent[] = [];
  grantedAgents: IGrantedAgent[] = [];
  pageSizes = KENDO_GRID_PAGE_SIZES;
  agentSelections: string[] = [];
  data = inject(MAT_DIALOG_DATA);
  pageSize = 10;
  total = 0;
  submitting = false;
  sensitivityLabelText = '';
  originalSensitivityLabel!: ISensitivityLabel;
  sort: SortDescriptor[] = [];
  sortIcon: SVGIcon = filterIcon;

  readonly excelSVG: SVGIcon = fileExcelIcon;
  readonly sortAscSvg: SVGIcon = chevronUpIcon;
  readonly sortDescSvg: SVGIcon = chevronDownIcon;
  readonly sensitivityLabel = new FormControl<ISensitivityLabel | null>(null, [Validators.required]);

  #apiService = inject(ApiService);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);
  #chatSessionService = inject(ChatSessionService);
  #agentService = inject(AgentService);

  constructor(private dialogRef: MatDialogRef<QuickEditSessionComponent>) {}

  ngOnInit(): void {
    // Initialize with current session's data
    const initialAgentSelections = this.#chatSessionService.selectedSession()?.agents ?? [];
    this.agentSelections = [...initialAgentSelections]; // Create a copy to avoid reference issues

    const sensitivityLabels = this.#chatSessionService.selectedSession()?.sensitivityLabels;

    if (sensitivityLabels && sensitivityLabels.length > 0) {
      this.sensitivityLabel.setValue(sensitivityLabels[0]);
      this.originalSensitivityLabel = sensitivityLabels[0];
      this.sensitivityLabelText = this.#agentService.getSensitivityLabelString(sensitivityLabels[0]);
    }

    // Pass initial selections to preserve them where possible
    this.#loadGrantedAgentsBySensitivityLabel(this.sensitivityLabel.value!, this.agentSelections);
  }

  ngAfterViewInit() {
    // Any initialization after view is loaded
  }

  public sortCheckboxColumn(): void {
    const isAscending = this.sort.length === 0 || this.sort[0].dir === 'desc';
    this.gridView = this.gridView.sort((a, b) => {
      const aSelected = this.agentSelections.includes(a.id);
      const bSelected = this.agentSelections.includes(b.id);

      if (aSelected && !bSelected) {
        return isAscending ? -1 : 1;
      } else if (!aSelected && bSelected) {
        return isAscending ? 1 : -1;
      } else {
        return 0;
      }
    });
    this.sort = [{ field: 'checkboxField', dir: isAscending ? 'asc' : 'desc' }];
    this.sortIcon = this.sort[0].dir === 'asc' ? this.sortAscSvg : this.sortDescSvg;
  }

  onSave() {
    this.#spinnerService.show();
    this.submitting = true;

    if (this.sensitivityLabel.value?.id !== 0) {
      const currentSession = this.#chatSessionService.selectedSession();

      if (!currentSession) {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
            message: 'No session selected to update',
            type: AlertEnum.error
        });
        return;
      }

      const updatedAgents = (this.agentSelections.length > 0)
          ? this.#filterAgentsWithSensitivityLabelAsSession(this.sensitivityLabel.value!, this.agentSelections)
          : this.agentSelections;

      const updatedSession: IChatSession = {
        ...currentSession,
        agents: updatedAgents,
        sensitivityLabels: [this.sensitivityLabel.value!]
      };

      this.#apiService.persistSession(updatedSession).subscribe({
        next: resp => {
          const isLabelChanged = (this.originalSensitivityLabel) ? !this.compareLabels(this.sensitivityLabel.value!, this.originalSensitivityLabel) : undefined;
          // Now update the selected session
          this.#chatSessionService.chatSessions.update(sessions => {
            const sessionIndex = sessions?.findIndex(session => session.id === updatedSession.id);
            if (sessionIndex !== undefined && sessionIndex !== -1) {
              sessions![sessionIndex] = updatedSession;
            } else {
              sessions?.push(updatedSession);
            }
            return sessions;
          });
          // for some reason, the computed() trigger doesn't seem to always be catching changes,
          // added the next few lines to make sure the update happens
          const chatSessions = this.#chatSessionService.chatSessions();
          if (chatSessions) {
            this.#chatSessionService.chatSessions.set(chatSessions);
            this.#chatSessionService.selectedSessionId.set(updatedSession.id);
          }

          this.dialogRef.close({
            result: true,
            agents: updatedAgents,
            sensitivityLabel: this.sensitivityLabel.value!,
            isLabelChanged
          });
        },
        error: (error: any) => {
          this.#alertService.showAlert({
              message: 'Failed to update session',
              type: AlertEnum.error
          });
          this.submitting = false;
          this.#spinnerService.hide();
        },
        complete: () => {
          this.submitting = false;
          this.#spinnerService.hide();
        }
      });
    } else {
      this.#spinnerService.hide();
      this.#alertService.showAlert({
          message: 'Please select a sensitivity label',
          type: AlertEnum.error
      });
      this.submitting = false;
    }
  }

  onFilter(value: Event): void {
    const inputValue = value;

    this.gridView = process(this.gridData, {
      filter: {
        logic: "or",
        filters: [
          {
            field: "name",
            operator: "contains",
            value: inputValue,
          },
          {
            field: "description",
            operator: "contains",
            value: inputValue,
          }
        ],
      },
    }).data;

    this.dataBinding()!.skip = 0;
    this.total = this.gridView.length;
  }

  rowCallback = (context: RowClassArgs) => {
    if ((context.index % 2) === 0) {
      return 'even';
    } else {
      return 'odd';
    }
  };

  /**
   * Updates the Sensitivity Label when a new one is selected
   * This will also refresh the available agents based on the new sensitivity label
   */
  labelSelected(event: ISensitivityLabelExpanded) {
    this.sensitivityLabel.setValue(event);
    this.sensitivityLabelText = this.#agentService.getSensitivityLabelString(event);

    // Save the current non-always-available agent selections before loading new data
    const savedSelections = [...this.agentSelections];

    // Load new agents based on sensitivity label
    this.#loadGrantedAgentsBySensitivityLabel(event, savedSelections);
  }

  compareLabels(item1: ISensitivityLabel, item2: ISensitivityLabel): boolean {
    return item1.id === item2.id;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  #generateGridData(data: IGrantedAgent[]) {
    // First ensure all always-available agents are included in selections
    data.forEach((agent: IGrantedAgent) => {
      if (agent.alwaysAvailable === true && !this.agentSelections.includes(agent.id)) {
        this.agentSelections.push(agent.id);
      }
    });

    // Then map the data for display
    return data.map((agent: IGrantedAgent) => {
      let sensitivityLabelsList = '';
      if (Object.hasOwn(agent, 'sensitivityLabels')) {
        sensitivityLabelsList = agent.sensitivityLabels!.map(label => label.label).join(', ');
      }

      let owners = '';
      if (Object.hasOwn(agent, 'owners')) {
        owners = agent.owners!.join(', ');
      }

      let sensitivityLabels: ISensitivityLabel[] = [];
      if (Object.hasOwn(agent, 'sensitivityLabels')) {
        sensitivityLabels = agent.sensitivityLabels!;
      }

      return {
        ...agent,
        sensitivityLabelList: sensitivityLabelsList,
        ownerList: owners,
        sensitivityLabels
      };
    });
  }

  /**
   * Filters out any selected agent that may not have the correct Sensitivity Label as the session.
   */
  #filterAgentsWithSensitivityLabelAsSession(sensitivityLabel: ISensitivityLabel, selectedAgents: string[]): string[] {
    const grantedAgents = this.grantedAgents;
    return grantedAgents
      .filter(agent => selectedAgents.includes(agent.id) && agent.sensitivityLabels &&
        agent.sensitivityLabels.some(label => (label.guid && label.guid === sensitivityLabel.guid) || label.id === sensitivityLabel.id)
      )
      .map(agent => agent.id);
  }

  #loadGrantedAgentsBySensitivityLabel(sensitivityLabel: ISensitivityLabelExpanded | ISensitivityLabel, savedSelections?: string[]) {
    this.#apiService.granted(sensitivityLabel?.guid).subscribe({
      next: response => {
        this.grantedAgents = response;

        // If we have saved selections, update agentSelections to preserve user selections where possible
        if (savedSelections) {
          // Keep only saved selections that are still valid with the new sensitivity label
          this.agentSelections = savedSelections.filter(agentId =>
            response.some(agent =>
              agent.id === agentId &&
              agent.sensitivityLabels?.some(label =>
                (label.guid && label.guid === sensitivityLabel.guid) ||
                label.id === sensitivityLabel.id
              )
            )
          );
        }

        // Generate grid data, which will add always-available agents to selections
        this.gridData = this.#generateGridData(response);
        this.gridView = this.gridData;
        this.total = this.gridData.length;
      }
    });
  }
}
